import { gsap, ScrollTrigger } from '../global/gsap';
import initLazyVideo from '../utilities/lazy-video';

const initWorkFilter = (section) => {
    const filterToggles = section.querySelectorAll('.js-work-filter-toggle');
    const filterButtonContainer = section.querySelectorAll('.js-work-filter-buttons');
    const filterOverlay = section.querySelector('.js-work-filter-overlay');
    const filterButtons = section.querySelectorAll('.js-work-filter-button');
    const activeFilterEl = section.querySelector('.js-work-filter-active');
    const activeFilterCountEls = section.querySelectorAll('.js-active-filter-count');
    const cardsWrapEl = document.querySelector('.js-cards-wrap');
    const hideEls = document.querySelectorAll('.js-filter-hide');
    const taxonomies = [];

    // Create an array of taxonomies
    filterButtons.forEach((filter) => {
        if (!taxonomies.includes(filter.dataset.type)) {
            taxonomies.push(filter.dataset.type);
        }
    });

    // Update active filter counts
    function updateActiveFilterCounts(url) {
        const params = url.searchParams;
        const countCommaValues = (value) => value.split(',').filter(Boolean).length;

        const total = Array.from(params.values()).reduce((sum, value) => sum + countCommaValues(value), 0);

        activeFilterCountEls.forEach((el) => {
            if (total) {
                el.classList.remove('is-hidden');
                el.innerText = `(${total})`;
            } else {
                el.classList.add('is-hidden');
                el.innerText = '';
            }
        });
    }

    // Update pill buttons
    function updatePillBtns(url) {
        const params = url.searchParams;

        // Clear active buttons
        activeFilterEl.innerHTML = '';

        // Check for URL params
        if (params && params.size > 0) {
            // Loop over params and split into array
            params.forEach((value, key) => {
                const filters = value.split(',');

                // Loop over values array and create button accordingly
                filters.forEach((filter) => {
                    if (filter) {
                        const button = document.createElement('button');
                        button.classList.add(
                            'work-filter__active-button',
                            'js-work-filter-active'
                        );

                        // Capitalize all first letters, apart from 'and'
                        let buttonText = filter.replaceAll('-', ' ');
                        buttonText = buttonText
                            .split(' ') // Turn into an array of words
                            .map((word) => {
                                if (word.toLowerCase() === 'and') return word;
                                return word.charAt(0).toUpperCase() + word.slice(1);
                            })
                            .join(' '); // Put it back together

                        const span = document.createElement('span');
                        span.textContent = buttonText;
                        button.append(span);

                        button.dataset.type = key;
                        button.dataset.name = filter;

                        activeFilterEl.append(button);
                    }
                });
            });
        }
    }

    function fetchResults(url) {
        // Add working class
        document.body.classList.add('working');

        // Get search params
        const params = url.searchParams;

        // Get current content to animate out
        const currentContent = cardsWrapEl.querySelector('.js-cards');

        let loadedCards;
        let noContent;

        // Fetch html from rendered page
        fetch(url).then((response) => response.text()).then((html) => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            // Get content element
            const content = doc.querySelector('.js-cards');

            if (content) {
                // Get new content and hide
                loadedCards = content.querySelectorAll('.js-filter-card');
                noContent = content.querySelector('.js-no-results');

                if (loadedCards && loadedCards.length > 0) {
                    gsap.set(loadedCards, {
                        opacity: 0
                    });
                }

                if (noContent) {
                    gsap.set(noContent, {
                        opacity: 0
                    });
                }

                gsap.to(currentContent, {
                    opacity: 0,
                    duration: 0.25,
                    overwrite: true,
                    onStart: () => {
                        // Show/hide the featured projects and titles based on if current params exist
                        if ((params && params.size) && (hideEls && hideEls.length > 0)) {
                            gsap.to(hideEls, {
                                opacity: 0,
                                duration: 0.25,
                                onComplete: () => {
                                    hideEls.forEach((hideEl) => {
                                        hideEl.classList.add('hide');
                                    });
                                }
                            });
                        } else if (hideEls && hideEls.length > 0) {
                            gsap.to(hideEls, {
                                opacity: 1,
                                duration: 0.25,
                                onStart: () => {
                                    hideEls.forEach((hideEl) => {
                                        hideEl.classList.remove('hide');
                                    });
                                }
                            });
                        }
                    },
                    onComplete: () => {
                        // Clear card wrap element
                        cardsWrapEl.innerHTML = '';

                        // Add new content to wrap element
                        cardsWrapEl.insertBefore(content, null);

                        // Fade in new content
                        if (loadedCards && loadedCards.length > 0) {
                            ScrollTrigger.batch(loadedCards, {
                                invalidateOnRefresh: true,
                                start: 'top 95%',
                                onEnter: (batch) => gsap.to(
                                    batch, {
                                        duration: 0.7,
                                        opacity: 1,
                                        stagger: 0.05,
                                        ease: 'power2'
                                    }
                                )
                            });
                        }

                        if (noContent) {
                            gsap.to(noContent, {
                                opacity: 1,
                                overwrite: true
                            });
                        }

                        // Initiate any new lazy load videos
                        const lazyVideoEls = cardsWrapEl.querySelectorAll('video.js-lazy');

                        if (lazyVideoEls && lazyVideoEls.length > 0) {
                            initLazyVideo(lazyVideoEls);
                        }

                        // Force ST refresh
                        window.dispatchEvent(new Event('resize'));
                        ScrollTrigger.refresh();

                        updatePillBtns(url);
                        updateActiveFilterCounts(url);

                        document.body.classList.remove('working');

                        // Scroll to top
                        gsap.to(window, { duration: 0.5, scrollTo: 0 });
                    }
                });
            }
        }).catch((err) => {
            // There was an error
            console.warn('Something went wrong.', err);
        });
    }

    // Update filters logic
    function updateFilters(button, filterType, filterTerm) {
        // Get current URL params
        let resultsUrl = new URL(window.location.href);
        const resultsUrlParams = resultsUrl.searchParams;

        if (button) {
            // If url params already has matching taxonomy type
            if (resultsUrlParams.has(filterType)) {
                const currentCatParam = resultsUrlParams.get(filterType);

                // If clicked tag is not already in params, add it with trailing comma
                // And add active class
                if (!currentCatParam.includes(filterTerm)) {
                    const newCatParams = `${currentCatParam}${filterTerm},`;
                    resultsUrl.searchParams.set(filterType, newCatParams);
                    button.classList.add('is-active');
                } else {
                    // Set empty new params variable
                    let newCatParams = '';

                    // If clicked tag is already set, remove it from string
                    if (currentCatParam.includes(`${filterTerm},`)) {
                        newCatParams = currentCatParam.replace(
                            `${filterTerm},`,
                            ''
                        );
                    }

                    // Update search param accordingly and remove active class
                    resultsUrl.searchParams.set(filterType, newCatParams);
                    button.classList.remove('is-active');
                }

                // If all taxonomy params are removed, delete it from params
                if (!resultsUrlParams.get(filterType)) {
                    resultsUrl.searchParams.delete(filterType);
                }
            } else {
                button.classList.add('is-active');
                resultsUrl.searchParams.append(filterType, `${filterTerm},`);
            }
            // Otherwise, trigged by reset button
        } else {
            resultsUrl = `${document.body.dataset.site}/${document.body.dataset.page}/`;

            filterButtons.forEach((filterButton) => {
                filterButton.classList.remove('is-active');
            });
        }

        window.history.pushState(history.state, '', resultsUrl);

        fetchResults(resultsUrl);
    }

    // Toggle function to show/hide overlay
    const toggleOverlay = () => {
        filterOverlay.classList.toggle('is-active');
    };

    // Function for selecting filters
    const handleFilterButton = (button) => {
        const { type, name } = button.dataset;
        updateFilters(button, type, name);
    };

    // Event handlers
    filterButtons.forEach((button) => {
        button.addEventListener('click', () => {
            handleFilterButton(button);
        });
    });

    // Active button event listener
    if (activeFilterEl) {
        activeFilterEl.addEventListener('click', (event) => {
            if (event.target.dataset.name) {
                const removedFilter = event.target.dataset.name;
                const removedType = event.target.dataset.type;
                const inputToUpdate = filterOverlay.querySelector(
                    `[data-type='${removedType}'][data-name='${removedFilter}']`
                );
                inputToUpdate.click();
            }
        });
    }

    filterToggles.forEach((toggle) => {
        toggle.addEventListener('click', () => {
            toggleOverlay();
        });
    });

    const toggleTl = gsap.timeline({
        scrollTrigger: {
            trigger: document.querySelector('footer'),
            start: 'top 75%',
            toggleActions: 'play none none reverse',
        },
    });

    toggleTl.to(filterButtonContainer, {
        autoAlpha: 0,
        yPercent: 50,
        ease: 'power3.inOut',
        duration: 0.475,
    });

    filterOverlay.addEventListener('click', (event) => {
        if (event.target.classList.contains('js-work-filter-overlay')) {
            toggleOverlay();
        }
    });

    // Pop state logic
    window.addEventListener('popstate', (event) => {
        const popUrl = new URL(event.target.location.href);
        fetchResults(popUrl);
    });

    // Initial load pill buttons
    const loadUrl = new URL(window.location.href);

    updatePillBtns(loadUrl);
    updateActiveFilterCounts(loadUrl);
};

export default initWorkFilter;
